import type { GetRecommendationsData } from '@vizit/api';
import {
  Badge,
  BadgeLevel,
  BadgeShape,
  Caption,
  ScoreSticker,
  ScoreStickerSize,
  Skeleton,
  Text,
  TextColor,
  TextSize,
  TextWeight,
} from '@vizit/atoms';
import { RETAILER_CONFIG_MAP } from '@vizit/constants';
import type { RetailerKey } from '@vizit/constants';
import type { OptimizationRecommendationDataMap } from '../optimization-recommendation-card';
import { ScoreExplain, getFactorsFromRecommendations, getFactorsFromRecommendationsV2 } from '../score-explain';
import { ProductHeaderInfoItem } from './product-header.info-item';

type PendingState<T = string> = {
  value: T;
  isPending: boolean;
};

interface ProductHeaderProps {
  /**
   * The name of the product with pending state
   * @required
   */
  name: PendingState<string>;
  /**
   * The Vizit listing score of the product with pending state
   * @required
   */
  listingScore: PendingState<number>;
  /**
   * The score change (difference) of the product with pending state
   * @optional
   */
  scoreChange: PendingState<number>;
  /**
   * The retailer of the product
   * @optional
   */
  retailer: string;
  /**
   * The ASIN of the product
   * @optional
   */
  itemId: string;
  /**
   * The brand of the product with pending state
   * @optional
   */
  brand: PendingState<string>;
  /**
   * The category of the product with pending state
   * @optional
   */
  category: PendingState<string>;
  /**
   * Whether the product is pre-launch
   * @default false
   * @optional
   */
  isPreLaunch: boolean;
  /**
   * Whether the data (other than the score) is still loading
   * @default false
   */
  isDataLoading?: boolean;
  /**
   * Optimization recommendations data for computing factor statuses
   * @optional
   */
  optimizations?: {
    recommendations: OptimizationRecommendationDataMap | null;
    recommendationsV2: GetRecommendationsData | null;
    isLoading: boolean;
    isV2Enabled: boolean;
    isPollingForUpdatesV2?: boolean;
  };
  /**
   * The hero image score for V1 recommendations
   * @optional
   */
  heroScore: number | null;
}

function ProductHeader(props: ProductHeaderProps) {
  const {
    name,
    listingScore,
    scoreChange,
    retailer,
    itemId,
    brand,
    category,
    isPreLaunch = false,
    isDataLoading = false,
    optimizations,
    heroScore,
  } = props;

  const retailerConfig = RETAILER_CONFIG_MAP[retailer as RetailerKey] ?? null;

  // Extract metrics from recommendationsV2, handling both 200 (with metrics) and 202 (processing) responses
  const metricsV2 =
    optimizations?.recommendationsV2 && 'metrics' in optimizations.recommendationsV2
      ? optimizations.recommendationsV2.metrics
      : undefined;

  const factors = optimizations?.isV2Enabled
    ? getFactorsFromRecommendationsV2(metricsV2)
    : getFactorsFromRecommendations(optimizations?.recommendations, heroScore ?? 0);

  // Show loading when:
  // 1. Initially loading data (isLoading)
  // 2. Polling for updates after receiving 202 response (isPollingForUpdatesV2)
  const isFactorsLoading = optimizations?.isLoading || optimizations?.isPollingForUpdatesV2 || false;

  return (
    <Caption
      label="Product Page info"
      infoCustomComponent={
        <ScoreExplain
          score={listingScore.value}
          factors={factors}
          isScoreLoading={listingScore.isPending}
          isFactorsLoading={isFactorsLoading}
        />
      }
    >
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex flex-row gap-12 flex-1 items-center justify-center min-h-[200px]">
          <ScoreSticker
            score={listingScore.value}
            size={ScoreStickerSize.Lg}
            isLoading={listingScore.isPending}
            scoreDiff={scoreChange.isPending ? null : scoreChange.value || null}
          />
          <div className="flex flex-col gap-12 flex-1">
            <div className="min-h-[60px] w-full">
              {name.isPending ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-12 w-full" variant="square" />
                  <Skeleton className="h-12 w-1/4" variant="square" />
                </div>
              ) : (
                <Text
                  elementType="h1"
                  size={TextSize.Lg}
                  color={TextColor.Secondary}
                  weight={TextWeight.Medium}
                  maxLines={3}
                >
                  {name.value || 'Unnamed product'}
                </Text>
              )}
            </div>
            <div className="grid grid-cols-4 gap-12 w-full md:w-3/4">
              {!isPreLaunch && (
                <>
                  <ProductHeaderInfoItem
                    label="Retailer"
                    value={retailerConfig?.displayName || retailer}
                    isLoading={isDataLoading}
                  />
                  <ProductHeaderInfoItem
                    label={retailerConfig?.itemIdLabel || 'ASIN'}
                    value={itemId}
                    isLoading={isDataLoading}
                  />
                </>
              )}

              <ProductHeaderInfoItem label="Brand" value={brand.value} isLoading={brand.isPending} />
              <ProductHeaderInfoItem label="Category" value={category.value} isLoading={category.isPending} />
              {isPreLaunch && (
                <Badge className="w-max h-fit" level={BadgeLevel.Default} shape={BadgeShape.Box}>
                  Pre-launch
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    </Caption>
  );
}

export { ProductHeader };
export type { ProductHeaderProps, PendingState };
